import React from 'react';
import { IPhraseTextProps } from './types';
import { theme } from '../../../../../../common/styles/theme';
import { TextWithSpeakerHOC } from '../../../../../../common/components/TextWithSpeaker/hocs/TextWithSpeakerHOC';
import { Text } from '../../../../../../common/components/Text/Text';
import { HighLightedText } from '../../../../../../common/components/HighLightedText/HighLightedText';
import { alpha, Box, Button, Stack, SxProps, Tooltip } from '@mui/material';
import { PencilIcon } from '../../../../../../../icons/PencilIcon';
import { TranslationsIcon } from '../../../../../../../icons/TranslationsIcon';
import { ContextEditIcon } from '../../../../../../../icons/ContextEditIcon';
import { ContextAddIcon } from '../../../../../../../icons/ContextAddIcon';
import { ContextRemoveIcon } from '../../../../../../../icons/ContextRemoveIcon';
import { SelectionUtil } from '../../../../../../common/utils/selection-util';
import { HtmlUtils } from '../../../../../../common/utils/html-utils';

export const PhraseText: React.FC<IPhraseTextProps> = ({
	context,
	text,
	fromLangCode,
	isContext,
	isPhrase,
	isTranslation,
	isTranslateContext,
	isEdit,
	isContextEditable,
	autoSpeak,
	onSelection,
}) => {
	if (!text) return null;

	const className = `phrase-text`;

	const buttonStyle: SxProps = {
		width: theme.spacing(3.5),
		height: theme.spacing(3.5),
		color: theme.palette.grey[400],

		'&:hover': {
			color: theme.palette.grey[700],
			background: alpha(theme.palette.grey[50], .5),
		},

		'&.active': {
			color: theme.palette.common.white,
			background: theme.palette.primary.main,
		},

		'.MuiSvgIcon-root': {
			width: theme.spacing(2.5),
			height: theme.spacing(2.5),
		},
	};

	const spaces = [' ', '.', ',', '!', '?', ':', ';', '"'];
	const mouseIsDown = React.useRef(false);
	const selectDebounce = React.useRef<any>(null);

	const onSelectEvent = React.useCallback(() => {
        if (selectDebounce.current) clearTimeout(selectDebounce.current);
		selectDebounce.current = setTimeout(() => {
			if (mouseIsDown.current) {
				return;
			}
			const selection = window.getSelection();
			const anchorNode = selection?.anchorNode;
			const focusNode = selection?.focusNode;
			if (!anchorNode || !focusNode) {
				return;
			}

			let parentsClasses = HtmlUtils.getParentsClasses(anchorNode as HTMLElement, [className]);
			if (!parentsClasses.includes(className)) return;

			parentsClasses = HtmlUtils.getParentsClasses(focusNode as HTMLElement, [className]);
			if (!parentsClasses.includes(className)) {
				SelectionUtil.removeWindowSelection();
				return;
			}

			let text = selection?.toString();
			const anchorContent = anchorNode.textContent || '';
			const focusContent = focusNode.textContent || '';

			if (selection.anchorOffset > 0) {
				let i = selection.anchorOffset;
				while (i > 0 &&
					!spaces.includes(anchorContent[i]) && !spaces.includes(anchorContent[i - 1])
				) {
					text = anchorContent[i - 1] + text;
					i--;
				}
			}
			if (selection.focusOffset < focusContent.length - 1) {
				let i = selection.focusOffset;
				while (i < focusContent.length) {
					if (spaces.includes(focusContent[i])) {
						break;
					}
					text += focusContent[i];
					i++;
				}
			}

			if (text) {
				if (onSelection) onSelection(text);
			}
		}, 300);
	}, []);

	const onMouseDown = () => {
		mouseIsDown.current = true;
	}
	const onMouseUp = () => {
		mouseIsDown.current = false;
		if (onSelection) onSelectEvent();
	}
	const onDoubleClick = () => {
		if (selectDebounce.current) clearTimeout(selectDebounce.current);
	}

	const handleContextTranslate = () => {
		if (onSelection) onSelection(context);
	}

	React.useEffect(() => {
		if (onSelection) document.addEventListener('selectionchange', onSelectEvent);

		return () => {
			if (selectDebounce.current) clearTimeout(selectDebounce.current);
			document.removeEventListener('selectionchange', onSelectEvent);
		}
	}, [isEdit]);

	const phrase = isContext ? context : text;
	const isLong = phrase.length > 100;

	return (<Stack
		className={`${className} ${isPhrase ? 'phrase' : 'selected'}
		 	${isContext ? 'context' : 'text'}
			${isTranslation && isEdit ? 'edit' : ''}
		`}
		sx={{
			'&.edit': {
				opacity: .5,
				cursor: 'pointer',

				'.text-with-speaker': {
					pointerEvents: 'none',
				},
			},

			'.speaker': {
				top: theme.spacing(.5),
			},
		}}
		onMouseDown={onMouseDown}
		onMouseUp={onMouseUp}
		onDoubleClick={onDoubleClick}
	>
		<TextWithSpeakerHOC
			autoSpeak={autoSpeak}
			lang={fromLangCode}
			children={
				<Text
					className={`${isLong ? 'long' : ''}`}
					sx={{
						fontSize: theme.typography.h5,
						fontWeight: 400,

						'&:before': {
							content: '""',
							position: 'absolute',
							left: theme.spacing(-3),
							width: theme.spacing(3),
							top: 0,
							bottom: 0,
						},

						'&.long': {
							fontSize: theme.typography.subtitle2,
						},

						[`.${className}.selected & .highlighted-span`]: {
							// background: `linear-gradient(0deg, #d7aeff 15%, ${alpha(theme.palette.info[100], .5)} 20%)`,
							background: 'none',
						},

						[`.${className}.phrase.context &`]: {
							color: theme.palette.grey[400],
							fontWeight: 300,

							'.highlighted-span': {
								color: theme.palette.grey[900],
								fontWeight: 400,
								background: 'none',
							}
						},

						[`&:hover .${className}__buttons`]: {
							opacity: 1,
							pointerEvents: 'all',
						},
					}}
				>
					{isContext
						? <HighLightedText
							text={context}
							highLighted={[text]}
						/>
						: text
					}

					{isContextEditable && !isEdit &&
						<Stack
							className={`${className}__buttons class-controll`}
							flexDirection={'row'}
							alignItems={'center'}
							gap={theme.spacing(.25)}
							sx={{
								display: 'inline-flex',
								ml: theme.spacing(.5),
								height: theme.spacing(2),
								transform: isTranslation ? 'translateY(4px)' : '',

								opacity: .1,
								pointerEvents: 'none',
								transition: 'opacity .3s ease',
							}}
						>
							{/* {isTranslation &&
								<Tooltip
									title={'Change phrase'}
								>
									<Button
										sx={buttonStyle}
										onClick={(e) => {
											e.preventDefault();
											onEdit(phrase);
										}}
									>
										<PencilIcon
											sx={{
												width: `${theme.spacing(2)} !important`,
												height: `${theme.spacing(2)} !important`,
											}}
										/>
									</Button>
								</Tooltip>
							} */}

							{isContext && isTranslation &&
								<Tooltip
									title={'Translate context'}
								>
									<Button
										className={`${isTranslateContext ? 'active' : ''}`}
										onClick={handleContextTranslate}
										sx={buttonStyle}
									>
										<TranslationsIcon />
									</Button>
								</Tooltip>
							}

							{/* <Box
								sx={{
									width: theme.spacing(.5),
								}}
							/>

							{!isContext &&
								<Tooltip
									title={'Add context'}
								>
									<Button
										onClick={onContextModify}
										sx={buttonStyle}
									>
										<ContextAddIcon />
									</Button>
								</Tooltip>
							}

							{isContext &&
							<>
								<Tooltip
									title={'Edit context'}
								>
									<Button
										onClick={onContextModify}
										sx={buttonStyle}
									>
										<ContextEditIcon />
									</Button>
								</Tooltip>

								<Tooltip
									title={'Remove context'}
								>
									<Button
										onClick={onContextDelete}
										sx={buttonStyle}
									>
										<ContextRemoveIcon />
									</Button>
								</Tooltip>
							</>
							} */}
						</Stack>
					}
					
				</Text>
			}
			text={String(text)}
		/>

	</Stack>);

}
